// The recipe editor, shared by the Recipe tab of the product dialog and the
// standalone Recipe dialog (recipe-only flavors). useRecipeEditor holds the
// form, the live cost / ingredient list / allergen math and the save;
// RecipeEditorFields draws the form and RecipeSaveConfirm asks whether to
// update the products' ingredient lists. Callers decide when to close and
// when to publish.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Switch } from '../../lib/shadcn/switch'
import { Plus, Trash2, AlertTriangle, Layers, GripVertical } from 'lucide-react'
import {
  useGetIngredients,
  useGetLabelRecipe,
  useGetRecipeLabels,
  useSaveLabelRecipe,
  useUpsertIngredient,
} from '../../hooks/backend/recipes'
import { useGetUnits, useGetFixedCosts } from '../../hooks/backend/settingsPage'
import { toast } from '../../lib/shadcn/sonner'
import IngredientCombobox, { type IngredientOption } from './IngredientCombobox'
import UnitSelect from './UnitSelect'
import RecipeCostSummary from './RecipeCostSummary'
import type { Product } from './ProductTable'
import { buildIngredientStatement, LABEL_INGREDIENTS_MAX } from '../../utils/ingredientStatement'
import { detectAllergens, rollUpAllergens, formatAllergens } from '../../utils/allergens'
import { unitMap, convertQuantity, predominance, densityGramsPerMl, type Unit } from '../../utils/units'
import { STATIONS } from '../../utils/stations'

export interface RecipeEditorOptions {
  product: Product | null
  /**
   * A recipe-only flavor (no product has this Label Name, e.g. the green apple
   * popcorn in Caramel Apple), opened without a product. Ignored when
   * `product` is set.
   */
  flavorName?: string | null
  /** Start a new recipe-only flavor: the form asks for its name. */
  createNew?: boolean
  /** Load the recipe when this turns true (the dialog or tab is showing). */
  active: boolean
}

export interface RecipeSaveResult {
  productsUpdated?: number
  blendsUpdated?: string[]
  publishLabels?: string[]
}

/** How a save goes: straight through, straight through without re-publishing
 * unchanged products, or ask first (current vs new ingredients). */
export type RecipeSaveMode = 'plain' | 'changedOnly' | 'confirm'

interface IngredientRow {
  id: string
  name: string
  list_name?: string | null
  cost: number | null
  cost_unit: string | null
  sub_ingredients: string | null
  allergens: string | null
  density_weight: number | null
  density_weight_unit: string | null
  density_volume: number | null
  density_volume_unit: string | null
}

interface RecipeLineData {
  id: string
  ingredient_id: string | null
  sub_label_name: string | null
  quantity: number | null
  unit: string | null
}

// A flavor with a recipe, resolved per oz (see getRecipeLabels), so a blend
// line can be costed and expanded into its ingredients.
interface FlavorRecipe {
  label_name: string
  line_count: number
  base_batch_weight_oz: number | null
  cost_per_oz: number | null
  cost_missing: boolean
  components: {
    ingredient_id: string
    name: string
    list_name?: string | null
    sub_ingredients: string | null
    allergens: string | null
    grams_per_oz: number
  }[]
  components_missing: boolean
  includes: string[]
  /** How much volume a batch makes, so a blend line can measure it by volume. */
  batch_volume?: number | null
  batch_volume_unit?: string | null
  /** No product uses this Label Name. */
  recipe_only?: boolean
}

interface LabelRecipeData {
  label_name: string
  base_batch_weight_oz: number | null
  /** A blend of other flavors by percentage; each line's quantity is its %. */
  blend_by_percent?: boolean
  /** Production station (Tumbler, Caramelizer, Kettle), or null when not set. */
  station?: string | null
  batch_volume?: number | null
  batch_volume_unit?: string | null
  lines: RecipeLineData[]
}

// Percentage blends are stored as oz of a 100 oz batch, so 50% = 50 oz.
const PERCENT_BATCH_OZ = 100

const roundPct = (n: number) => Math.round(n * 100) / 100

// An even split of 100% across n flavors, to the hundredth, adding up to
// exactly 100 (3 flavors -> 33.33, 33.33, 33.34).
function evenSplit(n: number): string[] {
  if (n <= 0) return []
  const base = Math.floor(10000 / n)
  const extra = 10000 - base * n
  return Array.from({ length: n }, (_, i) => String((base + (i >= n - extra ? 1 : 0)) / 100))
}

// True when the flavor percentages are blank or still the even split, i.e.
// nobody has typed their own numbers, so they can be re-split.
function isDefaultSplit(quantities: string[]): boolean {
  if (quantities.every(q => q.trim() === '')) return true
  const even = evenSplit(quantities.length)
  return quantities.every((q, i) => q.trim() !== '' && Number(q) === Number(even[i]))
}

// Sets the flavor lines of a percentage blend to an even split.
function splitEvenly<T extends { sub_label_name: string; quantity: string }>(lines: T[]): T[] {
  const split = evenSplit(lines.filter(l => l.sub_label_name).length)
  let i = 0
  return lines.map(l => (l.sub_label_name ? { ...l, quantity: split[i++] ?? '' } : l))
}

interface Line {
  key: string
  ingredient_id: string
  /** Set for a blend line: another flavor's recipe instead of an ingredient. */
  sub_label_name: string
  quantity: string
  unit: string
}

let keySeq = 0
const nextKey = () => `line-${keySeq++}`

const toLine = (r: RecipeLineData): Line => ({
  key: nextKey(),
  ingredient_id: r.ingredient_id ?? '',
  sub_label_name: r.sub_label_name ?? '',
  quantity: r.quantity != null ? String(r.quantity) : '',
  unit: r.unit ?? '',
})


// What the form holds, to spot unsaved changes.
function snapshotOf(
  baseWeight: string,
  byPercent: boolean,
  station: string,
  lines: Line[],
  newName: string,
  batchVolume = '',
  batchVolumeUnit = '',
): string {
  return JSON.stringify({
    baseWeight: baseWeight.trim(),
    byPercent,
    station: byPercent ? '' : station,
    batchVolume: byPercent ? '' : batchVolume.trim(),
    batchVolumeUnit: byPercent || batchVolume.trim() === '' ? '' : batchVolumeUnit,
    newName: newName.trim(),
    lines: lines.map(l => [l.ingredient_id, l.sub_label_name, l.quantity.trim(), l.unit]),
  })
}

const EMPTY_SNAPSHOT = snapshotOf('', false, '', [], '')

export function useRecipeEditor({
  product,
  flavorName,
  createNew = false,
  active,
}: RecipeEditorOptions) {
  const getIngredients = useGetIngredients()
  const getLabelRecipe = useGetLabelRecipe()
  const copyLabelRecipe = useGetLabelRecipe()
  const getRecipeLabels = useGetRecipeLabels()
  const saveLabelRecipe = useSaveLabelRecipe()
  const upsertIngredient = useUpsertIngredient()
  const getUnits = useGetUnits()
  const getFixedCosts = useGetFixedCosts()

  const [baseWeight, setBaseWeight] = useState('')
  // A blend of other flavors by percentage (e.g. Caramel Apple = 50% Caramel +
  // 50% Green Apple) instead of ingredients with a batch weight.
  const [byPercent, setByPercent] = useState(false)
  // Where the flavor is made, for printing recipes by station ('' = not set).
  const [station, setStation] = useState('')
  // How much volume a batch makes (recipe-only flavors), so other recipes can
  // add this flavor by volume (e.g. 2 cups) instead of weight.
  const [batchVolume, setBatchVolume] = useState('')
  const [batchVolumeUnit, setBatchVolumeUnit] = useState('cup')
  const [lines, setLines] = useState<Line[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [recipeLoading, setRecipeLoading] = useState(false)
  // The form as it loaded (null while loading), for unsaved changes.
  const [baseline, setBaseline] = useState<string | null>(null)
  const loadRequest = useRef(0)

  const ingredients = (getIngredients.data as IngredientRow[] | undefined) ?? []
  const options: IngredientOption[] = ingredients.map(i => ({ id: i.id, name: i.name }))
  const ingById = useMemo(() => new Map(ingredients.map(i => [i.id, i])), [ingredients])
  const units = (getUnits.data as Unit[] | undefined) ?? []
  const uMap = useMemo(() => unitMap(units), [units])
  const fixedCosts = (getFixedCosts.data as { id: string; name: string; cost: number }[] | undefined) ?? []
  const fixedTotal = fixedCosts.reduce((sum, f) => sum + (f.cost ?? 0), 0)

  // Without a product this is a recipe-only flavor: no SKU, never sold; it's
  // only used as a flavor line in other recipes.
  const recipeOnly = !product && (createNew || !!flavorName)
  const labelName = product
    ? (product.label_name ?? '').trim()
    : createNew
      ? newName.trim()
      : (flavorName ?? '').trim()
  const hasLabel = labelName.length > 0 || (recipeOnly && createNew)

  // Other flavors' recipes that can be copied into this one.
  const flavorRecipes = (getRecipeLabels.data as FlavorRecipe[] | undefined) ?? []
  const flavorByName = useMemo(
    () => new Map(flavorRecipes.map(f => [f.label_name, f])),
    [flavorRecipes],
  )
  const copySources = flavorRecipes.filter(r => r.label_name !== labelName)
  // Flavors this one can include in a blend: not itself, and not a flavor that
  // already includes it (that would loop).
  const blendSources = copySources.filter(r => !(r.includes ?? []).includes(labelName))
  // Names a new recipe-only flavor can't take (the server also checks
  // products' Label Names and recipes without lines).
  const takenNames = useMemo(
    () => new Set(flavorRecipes.map(f => f.label_name.toLowerCase())),
    [flavorRecipes],
  )
  // Blends that include this flavor; saving to products updates them too.
  const usedInBlends = copySources
    .filter(r => (r.includes ?? []).includes(labelName))
    .map(r => r.label_name)


  const loadRecipe = useCallback(async () => {
    // The dialog is shared across products, so clear the last recipe right
    // away instead of showing it until this one loads.
    const request = ++loadRequest.current
    setBaseWeight('')
    setByPercent(false)
    setStation('')
    setBatchVolume('')
    setBatchVolumeUnit('cup')
    setLines([])
    setBaseline(null)
    if (!hasLabel || createNew) {
      setRecipeLoading(false)
      setBaseline(EMPTY_SNAPSHOT)
      return
    }
    setRecipeLoading(true)
    let recipe: LabelRecipeData | undefined
    try {
      recipe = (await getLabelRecipe.trigger({ label_name: labelName })) as
        | LabelRecipeData
        | undefined
    } finally {
      if (request === loadRequest.current) setRecipeLoading(false)
    }
    // A newer recipe was opened while this one loaded.
    if (request !== loadRequest.current) return
    setBaseWeight(
      recipe?.base_batch_weight_oz != null ? String(recipe.base_batch_weight_oz) : '',
    )
    setByPercent(recipe?.blend_by_percent === true)
    setStation(recipe?.station ?? '')
    const vol = recipe?.batch_volume != null ? String(recipe.batch_volume) : ''
    const volUnit = recipe?.batch_volume_unit ?? 'cup'
    setBatchVolume(vol)
    setBatchVolumeUnit(volUnit)
    const loaded = (recipe?.lines ?? []).map(toLine)
    setLines(loaded)
    const base = recipe?.base_batch_weight_oz != null ? String(recipe.base_batch_weight_oz) : ''
    const pct = recipe?.blend_by_percent === true
    setBaseline(snapshotOf(base, pct, recipe?.station ?? '', loaded, '', vol, volUnit))
  }, [hasLabel, createNew, labelName, getLabelRecipe])

  useEffect(() => {
    if (active && (product || recipeOnly)) {
      if (createNew) setNewName('')
      void getIngredients.trigger()
      void getUnits.trigger({})
      void getFixedCosts.trigger({})
      void getRecipeLabels.trigger({}, { skipCache: true })
      void loadRecipe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, product?.sku, flavorName, createNew])

  // Hidden (dialog closed, tab not opened yet): nothing counts as unsaved.
  useEffect(() => {
    if (!active) setBaseline(null)
  }, [active])

  // Replaces the form with another flavor's batch weight and ingredient lines.
  // Nothing is saved until Save recipe.
  const copyFrom = async (source: string) => {
    if (!source) return
    try {
      const recipe = (await copyLabelRecipe.trigger({ label_name: source })) as
        | LabelRecipeData
        | undefined
      const copied = recipe?.lines ?? []
      setBaseWeight(
        recipe?.base_batch_weight_oz != null ? String(recipe.base_batch_weight_oz) : '',
      )
      setByPercent(recipe?.blend_by_percent === true)
      if (recipe?.station) setStation(recipe.station)
      if (recipe?.batch_volume != null) {
        setBatchVolume(String(recipe.batch_volume))
        setBatchVolumeUnit(recipe.batch_volume_unit ?? 'cup')
      }
      setLines(copied.map(toLine))
      toast.success(
        `Copied ${copied.length} ingredient${copied.length === 1 ? '' : 's'} from ${source}. Change what's different, then save.`,
      )
    } catch (error) {
      toast.error(
        `Failed to copy recipe: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  const addLine = () =>
    setLines(prev => [
      ...prev,
      { key: nextKey(), ingredient_id: '', sub_label_name: '', quantity: '', unit: '' },
    ])

  // A blend line: another flavor's recipe, measured by weight.
  // In a percentage blend the shares default to an even split (2 flavors = 50%
  // each). Once a share is typed by hand it's kept, and a new flavor gets
  // what's left to reach 100%.
  const addFlavorLine = () => {
    const unused = blendSources.find(f => !lines.some(l => l.sub_label_name === f.label_name))
    setLines(prev => {
      const added = [
        ...prev,
        {
          key: nextKey(),
          ingredient_id: '',
          sub_label_name: unused?.label_name ?? '',
          quantity: '',
          unit: 'oz',
        },
      ]
      if (!byPercent) return added
      const current = prev.filter(l => l.sub_label_name).map(l => l.quantity)
      if (isDefaultSplit(current)) return splitEvenly(added)
      const left = roundPct(100 - current.reduce((sum, q) => sum + (Number(q) || 0), 0))
      const last = added[added.length - 1]
      if (left > 0 && last) last.quantity = String(left)
      return added
    })
  }

  // Switches between a recipe with a batch weight and a percentage blend,
  // carrying the flavor lines over: oz of the batch become its %, and a % of a
  // 100 oz batch becomes oz. A percentage blend only holds flavors, so
  // ingredient lines are dropped (nothing is saved until Save recipe).
  const changeMode = (toPercent: boolean) => {
    if (toPercent === byPercent) return
    if (toPercent) {
      const base = Number(baseWeight)
      const hasBase = baseWeight.trim() !== '' && !Number.isNaN(base) && base > 0
      const dropped = lines.filter(l => !l.sub_label_name && l.ingredient_id).length
      const kept = lines
        .filter(l => l.sub_label_name)
        .map(l => {
          const oz = flavorOz(l)
          return {
            ...l,
            quantity: hasBase && oz != null ? String(roundPct((oz / base) * 100)) : '',
            unit: 'oz',
          }
        })
      if (kept.length === 0) {
        const unused = blendSources[0]
        if (unused) {
          kept.push({ key: nextKey(), ingredient_id: '', sub_label_name: unused.label_name, quantity: '', unit: 'oz' })
        }
      }
      setLines(kept.every(l => l.quantity === '') ? splitEvenly(kept) : kept)
      setBaseWeight(String(PERCENT_BATCH_OZ))
      setByPercent(true)
      if (dropped > 0) {
        toast.info(
          `A percentage blend only uses flavors, so ${dropped} ingredient line${dropped === 1 ? ' was' : 's were'} removed. Cancel to keep them.`,
        )
      }
    } else {
      setBaseWeight(String(PERCENT_BATCH_OZ))
      setLines(prev => prev.map(l => ({ ...l, unit: 'oz' })))
      setByPercent(false)
    }
  }

  // Removing a flavor from an evenly split percentage blend re-splits the rest.
  const removeLine = (key: string) =>
    setLines(prev => {
      const next = prev.filter(l => l.key !== key)
      if (!byPercent) return next
      const current = prev.filter(l => l.sub_label_name).map(l => l.quantity)
      return isDefaultSplit(current) ? splitEvenly(next) : next
    })

  const updateLine = (key: string, patch: Partial<Line>) =>
    setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)))

  // Moves a line to another position; the order is saved with the recipe.
  const moveLine = (key: string, toIndex: number) =>
    setLines(prev => {
      const from = prev.findIndex(l => l.key === key)
      if (from < 0 || toIndex < 0 || toIndex >= prev.length || from === toIndex) return prev
      const next = [...prev]
      const [item] = next.splice(from, 1)
      if (!item) return prev
      next.splice(toIndex, 0, item)
      return next
    })

  // Only recipe-only flavors can be measured by volume in a blend.
  const canUseVolume = (label: string) => flavorByName.get(label)?.recipe_only === true
  const isVolumeUnit = (unit: string) => uMap.get(unit.trim().toLowerCase())?.unit_type === 'volume'

  // Switching a blend line's flavor to one that can't be measured by volume
  // puts it back on oz.
  const selectFlavor = (key: string, label: string) =>
    setLines(prev =>
      prev.map(l => {
        if (l.key !== key) return l
        const unit = isVolumeUnit(l.unit) && !canUseVolume(label) ? 'oz' : l.unit
        return { ...l, sub_label_name: label, unit }
      }),
    )

  // When an ingredient is chosen, default the unit to its cost unit if unset.
  const selectIngredient = (key: string, id: string) =>
    setLines(prev =>
      prev.map(l => {
        if (l.key !== key) return l
        const nextUnit = l.unit || ingById.get(id)?.cost_unit || ''
        return { ...l, ingredient_id: id, unit: nextUnit }
      }),
    )

  const handleCreateIngredient = async (name: string): Promise<string | undefined> => {
    try {
      const res = (await upsertIngredient.trigger({
        name,
      })) as { id: string }
      await getIngredients.trigger(undefined, { skipCache: true })
      return res.id
    } catch {
      toast.error('Failed to create ingredient')
      return undefined
    }
  }

  // Saves the recipe; apply = also rewrite the products' ingredient list and
  // allergens. Returns the server's answer (with the flavors to publish), or
  // null when it failed (already shown as a toast).
  const save = async (
    applyIngredients: boolean,
    changedOnly = false,
  ): Promise<RecipeSaveResult | null> => {
    if ((!product && !recipeOnly) || !labelName) return null
    const payload = lines
      .filter(l => l.ingredient_id || l.sub_label_name)
      .map(l => ({
        ingredient_id: l.sub_label_name ? null : l.ingredient_id,
        sub_label_name: l.sub_label_name || null,
        quantity: l.quantity.trim() === '' ? null : Number(l.quantity),
        unit: byPercent ? 'oz' : l.unit || null,
      }))
    try {
      const res = ((await saveLabelRecipe.trigger({
        label_name: labelName,
        base_batch_weight_oz: byPercent
          ? PERCENT_BATCH_OZ
          : baseWeight.trim() === '' ? null : Number(baseWeight),
        blend_by_percent: byPercent,
        // Percentage blends aren't printed by station, so they don't get one.
        station: byPercent ? null : station || null,
        // Only recipe-only flavors record a batch volume; others keep theirs.
        ...(recipeOnly
          ? {
              batch_volume: byPercent || batchVolume.trim() === '' ? null : Number(batchVolume),
              batch_volume_unit: byPercent || batchVolume.trim() === '' ? null : batchVolumeUnit,
            }
          : {}),
        lines: payload,
        apply_ingredients: applyIngredients,
        ...(changedOnly ? { changed_only: true } : {}),
        ...(createNew ? { create_new: true } : {}),
      })) ?? {}) as RecipeSaveResult
      const n = res.productsUpdated ?? 0
      const blends = res.blendsUpdated ?? []
      toast.success(
        n > 0
          ? `Recipe saved — ingredient list and allergens updated on ${n} product${n === 1 ? '' : 's'}` +
              (blends.length > 0 ? ` (${recipeOnly ? '' : 'including '}${blends.join(', ')})` : '')
          : createNew
            ? `${labelName} added as a recipe-only flavor`
            : 'Recipe saved',
      )
      setConfirmOpen(false)
      setBaseline(
        snapshotOf(baseWeight, byPercent, station, lines, newName, batchVolume, batchVolumeUnit),
      )
      return res
    } catch (error) {
      toast.error(
        `Failed to save recipe: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
      return null
    }
  }

  // Why the recipe can't be saved yet, or null when it can.
  const checkSave = (): string | null => {
    if ((!product && !recipeOnly) || !labelName) return 'This flavor needs a name first.'
    if (createNew && takenNames.has(labelName.toLowerCase())) {
      return `${labelName} is already a flavor. Pick another name.`
    }
    if (byPercent) {
      if (flavorLines.length === 0) return 'Add at least one flavor to the blend.'
      if (!percentOk) {
        return `The percentages add up to ${roundPct(percentTotal)}%. They need to total 100%.`
      }
    } else {
      if (batchVolume.trim() !== '' && !(Number(batchVolume) > 0)) {
        return 'Batch volume needs to be a number above 0, or left blank.'
      }
      const noVolume = flavorLines.find(
        l => isVolumeUnit(l.unit) && flavorByName.get(l.sub_label_name)?.batch_volume == null,
      )
      if (noVolume) {
        return `${noVolume.sub_label_name} is measured by volume but its recipe has no batch volume. Add one to ${noVolume.sub_label_name}'s recipe, or measure it by weight.`
      }
    }
    return null
  }

  // Saving asks whether to push the generated list to products; if there is
  // nothing to generate, it just saves the recipe.
  const saveMode = (): RecipeSaveMode => {
    // A recipe-only flavor has no products of its own, so there's only
    // something to update when a blend uses it.
    if (!ingredientStatement || (recipeOnly && usedInBlends.length === 0)) return 'plain'
    // The products already have this ingredient list and these allergens:
    // save without asking and without re-publishing them. Blends that use
    // this flavor are still updated (and published) if their own lists change.
    if (!recipeOnly && ingredientsUnchanged && allergensUnchanged) return 'changedOnly'
    return 'confirm'
  }

  const usedIds = lines.map(l => l.ingredient_id).filter(Boolean)

  // ─── Live cost math ─────────────────────────────────────────────────────────
  const activeLines = lines.filter(l => l.ingredient_id && !l.sub_label_name)
  const flavorLines = lines.filter(l => l.sub_label_name)
  // A blend line's weight in oz (null when it can't be converted).
  // In a percentage blend a line's % is its oz of a 100 oz batch. A volume
  // (e.g. 2 cups) uses the flavor's batch volume and batch weight.
  function flavorOz(l: Line): number | null {
    const qty = Number(l.quantity)
    if (l.quantity.trim() === '' || Number.isNaN(qty)) return null
    if (byPercent) return qty
    const unit = l.unit || 'oz'
    if (isVolumeUnit(unit)) {
      const f = flavorByName.get(l.sub_label_name)
      const base = f?.base_batch_weight_oz
      if (f?.batch_volume == null || base == null || base <= 0) return null
      const ml = convertQuantity(qty, unit, 'ml', uMap)
      const batchMl = convertQuantity(f.batch_volume, f.batch_volume_unit ?? '', 'ml', uMap)
      if (ml == null || batchMl == null || batchMl <= 0) return null
      return (ml / batchMl) * base
    }
    return convertQuantity(qty, unit, 'oz', uMap)
  }
  const percentTotal = flavorLines.reduce((sum, l) => {
    const qty = Number(l.quantity)
    return l.quantity.trim() === '' || Number.isNaN(qty) ? sum : sum + qty
  }, 0)
  const percentOk = Math.abs(percentTotal - 100) <= 0.01
  const ingredientBatchCost = activeLines.reduce((sum, l) => {
    const ing = ingById.get(l.ingredient_id)
    const qty = Number(l.quantity)
    if (!ing || ing.cost == null || Number.isNaN(qty)) return sum
    // Convert the line quantity into the ingredient's cost unit before costing,
    // using the ingredient's density to bridge weight↔volume when needed.
    const density = densityGramsPerMl(
      ing.density_weight, ing.density_weight_unit,
      ing.density_volume, ing.density_volume_unit, uMap,
    )
    const converted =
      l.unit && ing.cost_unit
        ? convertQuantity(qty, l.unit, ing.cost_unit, uMap, density)
        : qty
    if (converted == null) return sum
    return sum + converted * ing.cost
  }, 0)
  // Linked flavors cost their oz × that flavor's cost per oz.
  const batchCost = flavorLines.reduce((sum, l) => {
    const f = flavorByName.get(l.sub_label_name)
    const oz = flavorOz(l)
    if (!f || f.cost_per_oz == null || oz == null) return sum
    return sum + oz * f.cost_per_oz
  }, ingredientBatchCost)
  // Why the cost is only an estimate, one plain sentence per problem, so the
  // warning can say exactly what to fill in.
  const missingReasons: string[] = []
  for (const l of activeLines) {
    const ing = ingById.get(l.ingredient_id)
    if (!ing) {
      missingReasons.push('An ingredient on this recipe no longer exists. Pick it again.')
      continue
    }
    if (ing.cost == null) {
      missingReasons.push(`${ing.name} has no cost. Add one under Ingredients.`)
      continue
    }
    const qty = Number(l.quantity)
    if (Number.isNaN(qty) || !l.unit || !ing.cost_unit) continue
    const density = densityGramsPerMl(
      ing.density_weight, ing.density_weight_unit,
      ing.density_volume, ing.density_volume_unit, uMap,
    )
    if (convertQuantity(qty, l.unit, ing.cost_unit, uMap, density) != null) continue
    // Flag lines whose unit can't convert to the ingredient's cost unit.
    const lineType = uMap.get(l.unit.trim().toLowerCase())?.unit_type
    const costType = uMap.get(ing.cost_unit.trim().toLowerCase())?.unit_type
    if (!lineType || !costType) {
      missingReasons.push(`${ing.name}: "${!lineType ? l.unit : ing.cost_unit}" isn't a known unit.`)
    } else if (lineType !== costType) {
      missingReasons.push(
        `${ing.name} is measured in ${l.unit} (${lineType}) but priced per ${ing.cost_unit} (${costType}). ` +
          `Add a density to ${ing.name} under Ingredients, or measure it in a ${costType} unit here.`,
      )
    } else {
      missingReasons.push(`${ing.name}: can't convert ${l.unit} to ${ing.cost_unit}.`)
    }
  }
  for (const l of flavorLines) {
    const f = flavorByName.get(l.sub_label_name)
    if (!f) missingReasons.push(`${l.sub_label_name} has no recipe yet.`)
    else if (flavorOz(l) == null)
      missingReasons.push(
        byPercent
          ? `${l.sub_label_name} needs a percentage.`
          : l.quantity.trim() === ''
            ? `${l.sub_label_name} needs an amount.`
            : isVolumeUnit(l.unit)
              ? f.batch_volume == null
                ? `${l.sub_label_name} is measured by volume but its recipe has no batch volume. Add one to ${l.sub_label_name}'s recipe, or measure it by weight.`
                : `${l.sub_label_name} needs a base batch weight to convert ${l.unit} to oz.`
              : `${l.sub_label_name} needs a weight${canUseVolume(l.sub_label_name) ? ' or volume' : ''} amount (like oz or lb${canUseVolume(l.sub_label_name) ? ', or cups' : ''}).`,
      )
    else if (f.cost_missing || f.cost_per_oz == null)
      missingReasons.push(`${l.sub_label_name}'s own cost is an estimate. Open that flavor to see what it's missing.`)
  }
  const baseW = byPercent ? PERCENT_BATCH_OZ : Number(baseWeight)
  const validBase = byPercent || (baseWeight.trim() !== '' && !Number.isNaN(baseW) && baseW > 0)
  const costPerOz = validBase ? batchCost / baseW : null
  // Cost uses the measured bag weight when one is recorded.
  const actualWeightOz = product?.actual_weight_oz ?? null
  const weightOz = actualWeightOz ?? product?.weight_oz ?? null
  const price = product?.price ?? null
  const ingredientCost =
    costPerOz != null && weightOz != null ? costPerOz * weightOz : null
  const productCost = ingredientCost != null ? ingredientCost + fixedTotal : null
  const margin = productCost != null && price != null ? price - productCost : null
  const marginPct =
    margin != null && price != null && price > 0 ? (margin / price) * 100 : null
  if (!validBase) missingReasons.push('No base batch weight. Enter it above.')
  if (byPercent && flavorLines.length > 0 && !percentOk)
    missingReasons.push(`The percentages add up to ${roundPct(percentTotal)}%, not 100%.`)
  // A recipe-only flavor has no product, so there's no product weight to need.
  if (!recipeOnly && weightOz == null)
    missingReasons.push(`${product?.item_name ?? product?.sku ?? 'This product'} has no weight (oz).`)
  const incomplete = activeLines.length + flavorLines.length > 0 && missingReasons.length > 0

  // ─── Live FDA ingredient statement ──────────────────────────────────────────
  // Linked flavors are expanded into their ingredients, scaled by the oz used,
  // and merged with this recipe's own (e.g. popcorn from both halves of a blend).
  const statementParts = new Map<
    string,
    {
      name: string
      list_name?: string | null
      sub_ingredients: string | null
      allergens: string | null
      weight: number
    }
  >()
  const addPart = (
    id: string,
    part: {
      name: string
      list_name?: string | null
      sub_ingredients: string | null
      allergens: string | null
    },
    weight: number,
  ) => {
    const w = Number.isFinite(weight) ? Math.max(0, weight) : 0
    const existing = statementParts.get(id)
    if (existing) existing.weight += w
    else
      statementParts.set(id, {
        name: part.name,
        list_name: part.list_name ?? null,
        sub_ingredients: part.sub_ingredients,
        allergens: part.allergens,
        weight: w,
      })
  }
  for (const l of activeLines) {
    const ing = ingById.get(l.ingredient_id)
    if (!ing) continue
    const unit = l.unit || ing.cost_unit || null
    const density = densityGramsPerMl(
      ing.density_weight, ing.density_weight_unit,
      ing.density_volume, ing.density_volume_unit, uMap,
    )
    addPart(
      ing.id,
      ing,
      predominance(l.quantity.trim() === '' ? null : Number(l.quantity), unit, uMap, density),
    )
  }
  for (const l of flavorLines) {
    const f = flavorByName.get(l.sub_label_name)
    const oz = flavorOz(l) ?? 0
    for (const c of f?.components ?? []) addPart(c.ingredient_id, c, c.grams_per_oz * oz)
  }
  // Ingredient lists use each ingredient's list name when set (Popcorn
  // Kernels -> POPCORN); allergens are still detected from its own name.
  const statementInfo = buildIngredientStatement(
    [...statementParts.values()].map(p => ({ ...p, name: p.list_name?.trim() || p.name })),
  )
  const ingredientStatement = statementInfo.statement

  // ─── Allergens rolled up from ingredients & sub-ingredients ─────────────────
  const rolledUp = rollUpAllergens([...statementParts.values()])
  const recipeAllergens = formatAllergens(rolledUp)
  const currentAllergens = recipeOnly ? '' : (product?.allergens ?? '').trim()
  const currentIngredients = recipeOnly ? '' : (product?.ingredients ?? '').trim()
  const ingredientsUnchanged = currentIngredients === ingredientStatement.trim()
  const allergensUnchanged = currentAllergens === recipeAllergens
  // Allergens on the product today that the recipe doesn't produce; saving to
  // products would remove these.
  const droppedAllergens = detectAllergens({ sub_ingredients: currentAllergens })
    .allergens.filter(a => !rolledUp.allergens.includes(a))

  const dirty =
    baseline != null &&
    !recipeLoading &&
    snapshotOf(baseWeight, byPercent, station, lines, newName, batchVolume, batchVolumeUnit) !== baseline

  return {
    product,
    createNew,
    recipeOnly,
    labelName,
    hasLabel,
    newName,
    setNewName,
    copySources,
    blendSources,
    usedInBlends,
    flavorByName,
    options,
    units,
    lines,
    baseWeight,
    setBaseWeight,
    byPercent,
    station,
    setStation,
    batchVolume,
    setBatchVolume,
    batchVolumeUnit,
    setBatchVolumeUnit,
    canUseVolume,
    selectFlavor,
    recipeLoading,
    copying: copyLabelRecipe.loading,
    saving: saveLabelRecipe.loading,
    confirmOpen,
    setConfirmOpen,
    copyFrom,
    addLine,
    addFlavorLine,
    changeMode,
    removeLine,
    updateLine,
    moveLine,
    selectIngredient,
    handleCreateIngredient,
    usedIds,
    flavorLines,
    percentTotal,
    percentOk,
    batchCost,
    costPerOz,
    actualWeightOz,
    weightOz,
    price,
    ingredientCost,
    fixedTotal,
    productCost,
    margin,
    marginPct,
    missingReasons,
    incomplete,
    ingredientStatement,
    statementInfo,
    recipeAllergens,
    currentIngredients,
    currentAllergens,
    ingredientsUnchanged,
    allergensUnchanged,
    droppedAllergens,
    dirty,
    save,
    checkSave,
    saveMode,
  }
}

export type RecipeEditor = ReturnType<typeof useRecipeEditor>

// One line under the title: whose recipe this is.
export function recipeDescription(e: RecipeEditor): string {
  const { recipeOnly, createNew, labelName, hasLabel, product } = e
  return recipeOnly
    ? createNew
      ? 'A recipe-only flavor is made in-house and only used inside other recipes (e.g. the green apple popcorn in Caramel Apple). It has no SKU and is never sold or synced; add it to a recipe with Add flavor.'
      : `Recipe-only flavor “${labelName}” — no SKU, never sold. Its cost, ingredients and allergens flow into the recipes that use it.`
    : hasLabel
      ? `Shared recipe for “${labelName}” — applies to every product with this label (e.g. Large & Small).`
      : product
        ? `${product.item_name ?? product.sku} has no Label Name, so it can’t share a recipe. Add a label name to the product first.`
        : ''
}

export function RecipeEditorFields({
  editor,
  saveLabel = 'Save recipe',
  embedded = false,
  showCost = true,
}: {
  editor: RecipeEditor
  /** The button that saves, named in the hints. */
  saveLabel?: string
  /** Inside the product dialog, whose body already scrolls. */
  embedded?: boolean
  /** Show the cost / margin summary (the product dialog shows it on Pricing instead). */
  showCost?: boolean
}) {
  const {
    createNew, recipeOnly, hasLabel, newName, setNewName, copySources,
    blendSources, flavorByName, options, units, lines, baseWeight, setBaseWeight,
    byPercent, station, setStation, recipeLoading, copying, copyFrom, addLine,
    batchVolume, setBatchVolume, batchVolumeUnit, setBatchVolumeUnit, canUseVolume,
    selectFlavor,
    addFlavorLine, changeMode, removeLine, updateLine, moveLine, selectIngredient,
    handleCreateIngredient, usedIds, flavorLines, percentTotal, percentOk,
    ingredientStatement, recipeAllergens,
  } = editor
  // The line being dragged by its handle, if any.
  const [dragKey, setDragKey] = useState<string | null>(null)
  return (
    <>
        {!hasLabel ? (
          <div className="flex items-start gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />

            <span>
              This product needs a Label Name before a recipe can be defined.
              Recipes are shared by label so all sizes stay in sync.
            </span>
          </div>
        ) : (
          <div className={embedded ? 'space-y-4' : 'space-y-4 py-2 flex-1 overflow-y-auto min-h-0'}>
            {createNew ? (
              <div className="space-y-1.5">
                <Label htmlFor="flavor-name" className="text-sm font-medium text-muted-foreground">
                  Flavor name
                </Label>
                <Input
                  id="flavor-name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Green Apple"
                  className="w-64"
                  autoFocus
                />
              </div>
            ) : null}

            {copySources.length > 0 ? (
              <div className="space-y-1.5">
                <Label
                  htmlFor="copy-recipe"
                  className="text-sm font-medium text-muted-foreground"
                >
                  Copy from another flavor
                </Label>

                <NativeSelect
                  id="copy-recipe"
                  value=""
                  onChange={e => void copyFrom(e.target.value)}
                  disabled={copying || recipeLoading}
                  className="h-9 w-64"
                >
                  <option value="">
                    {copying
                      ? 'Copying…'
                      : lines.length > 0
                        ? 'Replace with…'
                        : 'Choose a flavor…'}
                  </option>
                  {copySources.map(r => (
                    <option key={r.label_name} value={r.label_name}>
                      {r.label_name} ({r.line_count} ingredient{r.line_count === 1 ? '' : 's'})
                    </option>
                  ))}
                </NativeSelect>

                <p className="text-xs text-muted-foreground">
                  Fills in its batch weight and ingredients. Nothing is saved until you click {saveLabel}.
                </p>
              </div>
            ) : null}

            {byPercent ? null : (
            <div className="space-y-1.5">
              <Label
                htmlFor="recipe-station"
                className="text-sm font-medium text-muted-foreground"
              >
                Station
              </Label>
              <NativeSelect
                id="recipe-station"
                value={station}
                onChange={e => setStation(e.target.value)}
                disabled={recipeLoading}
                className="h-9 w-64"
              >
                <option value="">Not set</option>
                {STATIONS.map(st => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </NativeSelect>
              <p className="text-xs text-muted-foreground">
                Where this flavor is made. Recipes print grouped by station from Products &gt;
                Production &gt; Print recipes.
              </p>
            </div>
            )}

            <div className="flex items-start gap-3 rounded-md border p-3">
              <Switch
                id="blend-by-percent"
                checked={byPercent}
                onCheckedChange={changeMode}
                disabled={recipeLoading || (!byPercent && blendSources.length === 0)}
              />
              <div className="space-y-0.5">
                <Label htmlFor="blend-by-percent" className="text-sm font-medium">
                  Blend of other flavors by percentage
                </Label>
                <p className="text-xs text-muted-foreground">
                  {byPercent
                    ? 'Made by mixing other flavors (products or recipe-only flavors). Enter each one\'s share; they must total 100%. Cost, ingredients, allergens and the production plan come from those flavors\' recipes.'
                    : 'Turn on for a flavor that is just other flavors mixed together, e.g. Caramel Apple = 50% Caramel + 50% Green Apple. No batch weight needed.'}
                </p>
              </div>
            </div>

            {byPercent ? null : (
            <div className="space-y-1.5">
              <Label
                htmlFor="base-weight"
                className="text-sm font-medium text-muted-foreground"
              >
                Base batch weight (oz)
              </Label>

              <Input
                id="base-weight"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={baseWeight}
                onChange={e => setBaseWeight(e.target.value)}
                placeholder={recipeLoading ? 'Loading…' : 'e.g. 64'}
                disabled={recipeLoading}
                className="w-40"
              />

              <p className="text-xs text-muted-foreground">
                Total weight this recipe yields. Used to derive cost per ounce.
              </p>
            </div>
            )}

            {byPercent || !recipeOnly ? null : (
            <div className="space-y-1.5">
              <Label
                htmlFor="batch-volume"
                className="text-sm font-medium text-muted-foreground"
              >
                Batch volume (optional)
              </Label>

              <div className="flex gap-2">
                <Input
                  id="batch-volume"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={batchVolume}
                  onChange={e => setBatchVolume(e.target.value)}
                  placeholder={recipeLoading ? 'Loading…' : 'e.g. 8'}
                  disabled={recipeLoading}
                  className="w-40"
                />
                <UnitSelect
                  value={batchVolumeUnit}
                  onChange={setBatchVolumeUnit}
                  units={units}
                  filterType="volume"
                  placeholder="Unit"
                  disabled={recipeLoading}
                  className="h-9 w-24"
                />
              </div>

              <p className="text-xs text-muted-foreground">
                How much this recipe makes by volume. Lets other recipes add this flavor
                by volume (like cups) as well as by weight.
              </p>
            </div>
            )}

            <div className="space-y-2">
              <div className="grid grid-cols-[1.25rem,1fr,5rem,6rem,2rem] gap-2 px-1 text-xs font-medium text-muted-foreground">
                <span />
                <span>{byPercent ? 'Flavor' : 'Ingredient or flavor'}</span>
                <span>{byPercent ? 'Share' : 'Quantity'}</span>
                <span>{byPercent ? '' : 'Unit'}</span>
                <span />
              </div>

              {recipeLoading ? (
                <p className="px-1 py-4 text-sm text-muted-foreground">
                  Loading recipe…
                </p>
              ) : lines.length === 0 ? (
                <p className="px-1 py-4 text-sm text-muted-foreground">
                  {byPercent ? 'No flavors yet. Add one below.' : 'No ingredients yet. Add one below.'}
                </p>
              ) : (
                <div className={embedded ? 'space-y-2' : 'space-y-2 max-h-[35vh] overflow-y-auto pr-1'}>
                  {lines.map((line, index) => (
                    <div
                      key={line.key}
                      data-line-row
                      onDragOver={e => {
                        if (!dragKey) return
                        e.preventDefault()
                        e.dataTransfer.dropEffect = 'move'
                        if (dragKey !== line.key) moveLine(dragKey, index)
                      }}
                      onDrop={e => {
                        e.preventDefault()
                        setDragKey(null)
                      }}
                      className={
                        dragKey === line.key
                          ? 'grid grid-cols-[1.25rem,1fr,5rem,6rem,2rem] gap-2 items-center rounded-md bg-muted/60 opacity-60'
                          : 'grid grid-cols-[1.25rem,1fr,5rem,6rem,2rem] gap-2 items-center'
                      }
                    >
                      <button
                        type="button"
                        draggable
                        onDragStart={e => {
                          setDragKey(line.key)
                          e.dataTransfer.effectAllowed = 'move'
                          e.dataTransfer.setData('text/plain', line.key)
                          const row = e.currentTarget.closest('[data-line-row]')
                          if (row instanceof HTMLElement) e.dataTransfer.setDragImage(row, 12, 18)
                        }}
                        onDragEnd={() => setDragKey(null)}
                        onKeyDown={e => {
                          if (e.key === 'ArrowUp' && index > 0) {
                            e.preventDefault()
                            moveLine(line.key, index - 1)
                          } else if (e.key === 'ArrowDown' && index < lines.length - 1) {
                            e.preventDefault()
                            moveLine(line.key, index + 1)
                          }
                        }}
                        className="flex h-9 w-5 cursor-grab items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
                        aria-label="Drag to reorder, or use the up and down arrow keys"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      {line.sub_label_name ? (
                        <div className="flex items-center gap-1.5">
                          <Layers
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-label="Flavor"
                          />
                          <NativeSelect
                            value={line.sub_label_name}
                            onChange={e => selectFlavor(line.key, e.target.value)}
                            className="h-9 w-full"
                            aria-label="Flavor"
                          >
                            {!flavorByName.has(line.sub_label_name) && (
                              <option value={line.sub_label_name}>{line.sub_label_name}</option>
                            )}
                            {blendSources
                              .filter(
                                f =>
                                  f.label_name === line.sub_label_name ||
                                  !lines.some(o => o.sub_label_name === f.label_name),
                              )
                              .map(f => (
                                <option key={f.label_name} value={f.label_name}>
                                  {f.label_name} (flavor)
                                </option>
                              ))}
                          </NativeSelect>
                        </div>
                      ) : (
                      <IngredientCombobox
                        ingredients={options}
                        value={line.ingredient_id}
                        onChange={id =>
                          selectIngredient(line.key, id)
                        }
                        onCreate={handleCreateIngredient}
                        excludeIds={usedIds.filter(
                          id => id !== line.ingredient_id
                        )}
                        className="w-full"
                      />
                      )}

                      <Input
                        type="number"
                        min="0"
                        {...(byPercent ? { max: '100' } : {})}
                        step="0.01"
                        inputMode="decimal"
                        value={line.quantity}
                        onChange={e =>
                          updateLine(line.key, {
                            quantity: e.target.value,
                          })
                        }
                        placeholder={byPercent ? '50' : '0'}
                        className="h-9"
                        aria-label={byPercent ? 'Percent' : 'Quantity'}
                      />

                      {byPercent ? (
                        <span className="px-1 text-sm text-muted-foreground">%</span>
                      ) : (
                      <UnitSelect
                        value={line.unit}
                        onChange={u =>
                          updateLine(line.key, {
                            unit: u,
                          })
                        }
                        units={units}
                        {...(line.sub_label_name
                          ? {
                              filterType: canUseVolume(line.sub_label_name)
                                ? (['weight', 'volume'] as ('weight' | 'volume')[])
                                : ('weight' as const),
                            }
                          : {})}
                        placeholder="Unit"
                        className="h-9"
                      />
                      )}

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          removeLine(line.key)
                        }
                        aria-label="Remove ingredient"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {byPercent ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLine}
                disabled={recipeLoading}
                className="mr-2 gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add ingredient
              </Button>
              )}

              {blendSources.length > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFlavorLine}
                  disabled={
                    recipeLoading ||
                    blendSources.every(f =>
                      lines.some(l => l.sub_label_name === f.label_name),
                    )
                  }
                  className="gap-1.5"
                >
                  <Layers className="h-4 w-4" />
                  Add flavor
                </Button>
              ) : null}

              {byPercent && flavorLines.length > 0 ? (
                <p
                  className={
                    percentOk
                      ? 'text-sm tabular-nums text-emerald-600 dark:text-emerald-500'
                      : 'text-sm tabular-nums text-amber-600 dark:text-amber-500'
                  }
                >
                  Total {roundPct(percentTotal)}%
                  {percentOk
                    ? ''
                    : percentTotal < 100
                      ? ` · ${roundPct(100 - percentTotal)}% left to assign`
                      : ` · ${roundPct(percentTotal - 100)}% over`}
                </p>
              ) : null}

              {!byPercent && flavorLines.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Flavor lines use that flavor&apos;s recipe by weight (or by volume for
                  recipe-only flavors with a batch volume), so its cost, ingredients and
                  allergens stay in sync when it changes.
                </p>
              ) : null}
            </div>

            {/* Cost / margin summary */}
            {showCost ? <RecipeCostSummary editor={editor} /> : null}

            {/* FDA ingredient statement preview */}
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-medium">
                  {recipeOnly ? 'Ingredient list' : 'Product ingredient list'}
                </span>

                <span className="text-xs text-muted-foreground">
                  {recipeOnly
                    ? 'Ordered by weight · merged into the recipes that use it'
                    : 'Ordered by weight · saved to all products with this label'}
                </span>
              </div>

              {ingredientStatement ? (
                <>
                <p className="leading-relaxed text-foreground">
                  {ingredientStatement}
                </p>
                <IngredientLength info={editor.statementInfo} />
                </>
              ) : (
                <p className="text-muted-foreground">
                  Add ingredients with quantities to generate the list.
                </p>
              )}
            </div>

            {/* Allergens preview */}
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-medium">
                  Allergens
                </span>

                <span className="text-xs text-muted-foreground">
                  Detected from ingredients &amp; sub-ingredients
                </span>
              </div>

              <p className={recipeAllergens ? 'leading-relaxed text-foreground' : 'text-muted-foreground'}>
                {recipeAllergens || 'None of the major allergens detected.'}
              </p>
            </div>
          </div>
        )}
    </>
  )
}

// "Update product ingredients & allergens?" with the current and new lists.
// A plain Dialog, not AlertDialog: AlertDialog bundles a different Radix copy,
// so the two layers don't know about each other and closing this one could
// leave the page with pointer-events: none.
export function RecipeSaveConfirm({
  editor,
  onSave,
  saving,
}: {
  editor: RecipeEditor
  /** apply = also rewrite the products' ingredient list and allergens. */
  onSave: (apply: boolean) => void
  saving: boolean
}) {
  const {
    recipeOnly, labelName, usedInBlends, ingredientsUnchanged, currentIngredients,
    ingredientStatement, allergensUnchanged, currentAllergens, recipeAllergens,
    droppedAllergens, confirmOpen, setConfirmOpen,
  } = editor
  return (
    <Dialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {recipeOnly
              ? 'Update ingredients & allergens on the blends?'
              : 'Update product ingredients & allergens?'}
          </DialogTitle>

          <DialogDescription>
            {recipeOnly
              ? `${labelName} is used in ${usedInBlends.join(', ')}. Do you want to regenerate the Ingredients and Allergens fields on their products from the updated recipe?`
              : <>
            This recipe generates an FDA-style ingredient list and
            an allergen list. Do you want to overwrite the
            Ingredients and Allergens fields on every product
            labeled “{labelName}” with them?
            {usedInBlends.length > 0
              ? ` Blends that use this flavor (${usedInBlends.join(', ')}) get their lists updated too.`
              : ''}
              </>}
          </DialogDescription>
        </DialogHeader>

        {recipeOnly ? null : (
        <div className="space-y-3 rounded-md border bg-muted/40 p-3 text-sm">
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ingredients{ingredientsUnchanged ? ' · no change' : ''}
            </p>
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-muted-foreground">Current</span>
              <span className="leading-relaxed">{currentIngredients || '—'}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-muted-foreground">New</span>
              <span className="font-medium leading-relaxed">{ingredientStatement || '—'}</span>
            </div>
          </div>

          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Allergens{allergensUnchanged ? ' · no change' : ''}
            </p>
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-muted-foreground">Current</span>
              <span>{currentAllergens || '—'}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-muted-foreground">New</span>
              <span className="font-medium">{recipeAllergens || 'None'}</span>
            </div>
            {droppedAllergens.length > 0 ? (
              <p className="flex items-start gap-1.5 pt-1 text-xs text-amber-600 dark:text-amber-500">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {droppedAllergens.join(', ')} will be removed. Check the
                  recipe isn’t missing an ingredient or sub-ingredient.
                </span>
              </p>
            ) : null}
          </div>
        </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setConfirmOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="outline"
            onClick={() => onSave(false)}
            disabled={saving}
          >
            Save recipe only
          </Button>

          <Button
            onClick={() => onSave(true)}
            disabled={saving}
          >
            {saving
              ? 'Saving…'
              : recipeOnly
                ? 'Save & update blends'
                : 'Save & update products'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Character count for the label, and a note when the list was shortened. */
function IngredientLength({ info }: { info: { statement: string; full: string; shortened: boolean } }) {
  const len = info.statement.length
  const over = len > LABEL_INGREDIENTS_MAX
  return (
    <p className={`mt-1.5 text-xs ${over ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>
      {len.toLocaleString()} / {LABEL_INGREDIENTS_MAX} characters
      {info.shortened
        ? ` · shortened from ${info.full.length.toLocaleString()}: ingredients under 2% grouped and repeats listed once`
        : ''}
      {over ? ' · too long for the label' : ''}
    </p>
  )
}
